import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes , Navigate} from 'react-router-dom';

const Home = lazy(() => import('./pages/Home'));
const Admission = lazy(() => import('./pages/Admission'));
const About = lazy(() => import('./pages/About'));
const Blog = lazy(() => import('./pages/Blog'));
const Bus = lazy(() => import('./pages/Bus'));
const Contact = lazy(() => import('./pages/Contact'));
const Facilities = lazy(() => import('./pages/Facilities'));
const Feedback = lazy(() => import('./pages/Feedback'));
const Fees = lazy(() => import('./pages/Fees'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Circulars = lazy(() => import('./pages/Circulars'));
const AdminCirculars = lazy(() => import('./pages/AdminCirculars'));
const General = lazy(() => import('./pages/General'));
const News = lazy(() => import('./pages/News'));
const Projects = lazy(() => import('./pages/Projects'));
const Services = lazy(() => import('./pages/Services'));
const Uniform = lazy(() => import('./pages/Uniform'));
const Video = lazy(() => import('./pages/Video'));
const OurTeachers = lazy(() => import('./pages/OurTeachers'));
const Administration = lazy(() => import('./pages/Administration'));
const Career = lazy(() => import('./pages/Career'));
const WhyChooseUs = lazy(() => import('./pages/WhyChooseUs'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Academics = lazy(() => import('./pages/Academics'));
const AdminNotices = lazy(() => import('./pages/AdminNotices'));
const AdminTicker = lazy(() => import('./pages/AdminTicker'));
const KidsCorner = lazy(() => import('./pages/KidsCorner'));
const KidsCornerBlog = lazy(() => import('./pages/KidsCornerBlog'));
const KidsCornerArt = lazy(() => import('./pages/KidsCornerArt'));
const KidsCornerActivities = lazy(() => import('./pages/KidsCornerActivities'));
const AdminKidsCorner = lazy(() => import('./pages/AdminKidsCorner'));
const Achievements = lazy(() => import('./pages/Achievements'));

// Admin Auth and Layout Imports
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminForgotPassword = lazy(() => import('./pages/AdminForgotPassword'));
const AdminResetPassword = lazy(() => import('./pages/AdminResetPassword'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
// Admin News Imports
const AdminNewsList = lazy(() => import('./pages/AdminNewsList'));
const AdminNewsForm = lazy(() => import('./pages/AdminNewsForm'));
// Admin Gallery Imports
const AdminGalleryList = lazy(() => import('./pages/AdminGalleryList'));
const AdminGalleryForm = lazy(() => import('./pages/AdminGalleryForm'));
// Additional Admin Imports
const AdminVideoList = lazy(() => import('./pages/AdminVideoList'));
const AdminVideoForm = lazy(() => import('./pages/AdminVideoForm'));
const AdminEnquiryList = lazy(() => import('./pages/AdminEnquiryList'));
const AdminTestimonials = lazy(() => import('./pages/AdminTestimonials'));

import ScrollToTop from './components/ScrollToTop';

import { installAxiosAuth } from './utils/authToken';
installAxiosAuth();

function App() {
  // Scroll Reveal Implementation using IntersectionObserver
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const targets = document.querySelectorAll('.mnps-reveal:not(.is-visible)');
    if (!targets.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -80px 0px' });
    targets.forEach((el) => obs.observe(el));
    // Re-scan on every route change by listening to popstate + a microtask after navigation
    const rescan = () => {
      const newTargets = document.querySelectorAll('.mnps-reveal:not(.is-visible)');
      newTargets.forEach((el) => obs.observe(el));
    };
    const rescanInterval = setInterval(rescan, 800);
    return () => {
      clearInterval(rescanInterval);
      obs.disconnect();
    };
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Suspense fallback={<div className="loading-overlay">Mathanagar Public School...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admission" element={<Admission />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/bus" element={<Bus />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Career />} />
            <Route path="/facilities" element={<Facilities />} />
            <Route path="/facility" element={<Navigate to="/facilities" replace />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/fees" element={<Fees />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/circulars" element={<Circulars />} />
            <Route path="/general" element={<General />} />
            <Route path="/news" element={<News />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/services" element={<Services />} />
            <Route path="/uniform" element={<Uniform />} />
            <Route path="/video" element={<Video />} />
            <Route path="/our-teachers" element={<OurTeachers />} />
            <Route path="/administration" element={<Administration />} />
            <Route path="/why-choose-us" element={<WhyChooseUs />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/kids-corner" element={<KidsCorner />} />
            <Route path="/kids-corner/blog" element={<KidsCornerBlog />} />
            <Route path="/kids-corner/art" element={<KidsCornerArt />} />
            <Route path="/kids-corner/activities" element={<KidsCornerActivities />} />
            <Route path="/academics" element={<Academics />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <AdminLayout><AdminDashboard /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/news" element={
              <ProtectedRoute>
                <AdminLayout><AdminNewsList /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/news/new" element={
              <ProtectedRoute>
                <AdminLayout><AdminNewsForm /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/news/:id/edit" element={
              <ProtectedRoute>
                <AdminLayout><AdminNewsForm /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/gallery" element={
              <ProtectedRoute>
                <AdminLayout><AdminGalleryList /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/gallery/new" element={
              <ProtectedRoute>
                <AdminLayout><AdminGalleryForm /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/gallery/:id/edit" element={
              <ProtectedRoute>
                <AdminLayout><AdminGalleryForm /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/videos" element={
              <ProtectedRoute>
                <AdminLayout><AdminVideoList /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/videos/new" element={
              <ProtectedRoute>
                <AdminLayout><AdminVideoForm /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/videos/:id/edit" element={
              <ProtectedRoute>
                <AdminLayout><AdminVideoForm /></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/notices" element={<ProtectedRoute><AdminLayout><AdminNotices /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/circulars" element={<ProtectedRoute><AdminLayout><AdminCirculars /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ticker" element={<ProtectedRoute><AdminLayout><AdminTicker /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/kids-corner" element={<ProtectedRoute><AdminLayout><AdminKidsCorner /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedRoute><AdminLayout><AdminTestimonials /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/enquiries" element={
              <ProtectedRoute>
                <AdminLayout><AdminEnquiryList /></AdminLayout>
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
